import React, { useEffect, useState, FC } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { connect } from "react-redux";
import { Outlet, Route, Routes, useLocation, useNavigate } from "react-router";
import "./app.css";
import ManagePermission from "./component/screen/administration/permission/managePermission";
import Permissions from "./component/screen/administration/permission/permissions";
import ManagePermissionGroup from "./component/screen/administration/permissionGroup/managePermissionGroup";
import PermissionGroup from "./component/screen/administration/permissionGroup/permissionGroup";
import ManageUserPermission from "./component/screen/administration/userPermission/manageUserPermission";
import UserPermissions from "./component/screen/administration/userPermission/userPermissions";
import Bundle from "./component/screen/bundle/bundle";
import ManageBundle from "./component/screen/bundle/manageBundle";
import CannedTestEdit from "./component/screen/cannedTests/cannedTestEdit";
import CannedTestImport from "./component/screen/cannedTests/cannedTestImport";
import CannedTests from "./component/screen/cannedTests/cannedTests";
import ClassifyCleanup from "./component/screen/classifyCleanup/classifyCleanup";
import Content from "./component/screen/content/content";
import ManageContent from "./component/screen/content/manageContent";
import CRM from "./component/screen/crm/crm";
import DistrictDetail from "./component/screen/crm/districtDetail";
import ManageCrm from "./component/screen/crm/manageCrm";
import SchoolDetail from "./component/screen/crm/schoolDetail";
import {
  Assessments,
  AssessmentEdit,
  AssessmentNew,
  Passages,
  Questions,
  QuestionEditorRoute,
  QuestionNew,
  TeacherPassageEditor,
  UserContent,
  UserContentComingSoon,
  UserContentLayout,
} from "./component/screen/crm/content";
import ActivityReport from "./component/screen/dashboard/activityReport";
import Dashboard from "./component/screen/dashboard/dashboard";
import Domain from "./component/screen/domain/domain";
import ManageDomain from "./component/screen/domain/manageDomain";
import DefaultSubjects from "./component/screen/galaxyStarsSubjects/galaxyStarsSubjects";
import Grade from "./component/screen/grade/grade";
import ManageGrade from "./component/screen/grade/manageGrade";
import HomeComponent from "./component/screen/home/home";
import { LearnosityResubmit } from "./component/screen/learnosityUtils/LearnosityResubmit";
import { LessonPlanAssetEdit } from "./component/screen/lessonPlans/LessonPlanAssetEdit";
import { LessonPlanEdit } from "./component/screen/lessonPlans/LessonPlanEdit";
import { LessonPlans } from "./component/screen/lessonPlans/LessonPlans";
import LiftOffSubject from "./component/screen/liftOffSubject/liftOffsubject";
import LoginComponent from "./component/screen/login/login";
import PassageQuestions from "./component/screen/passage/associatePassageQuestions";
import ManagePassage from "./component/screen/passage/managePassage";
import Passage from "./component/screen/passage/passage";
import AuthorQuestion from "./component/screen/questions/authorQuestion";
import ImageGallery from "./component/screen/questions/imageGallery/imageGallery";
import { PublicItemPreview } from "./component/screen/questions/PublicItemPreview";
import QuestionList from "./component/screen/questions/questionList";
import ManageSellableproduct from "./component/screen/sellableProduct/manageSellableProduct";
import Sellableproduct from "./component/screen/sellableProduct/sellableproduct";
import ManagePracticeQuestions from "./component/screen/skills/managePracticeQustion";
import ManageSkillsPracticeStandards from "./component/screen/skills/managePracticeStandards";
import ManageSkillsPracticeSubject from "./component/screen/skills/managePracticeSubjects";
import PracticeQuestions from "./component/screen/skills/practiceQuestions";
import SkillsPracticeStandards from "./component/screen/skills/practiceStandards";
import SkillsPracticeSubject from "./component/screen/skills/practiceSubjects";
import AccociatedQuestions from "./component/screen/standard/associateQuestions";
import ManageStandard from "./component/screen/standard/manageStandard";
import Standard from "./component/screen/standard/standard";
import MagicMatch from "./component/screen/standard/standardMagicMatch";
import StandardGroupEdit from "./component/screen/standardGroup/manageStandardGroup";
import StandardGroup from "./component/screen/standardGroup/standardGroup";
import StandardLink from "./component/screen/standardLink/standardLink";
import SubjectDetails from "./component/screen/subject/manageSubject";
import Subject from "./component/screen/subject/subject";
import ManageSupersheet from "./component/screen/supersheet/manageSupersheet";
import Supersheet from "./component/screen/supersheet/supersheet";
import ManageTag from "./component/screen/tag/manageTag";
import Tag from "./component/screen/tag/tag";
import TeacherQuestionList from "./component/screen/teacherQuestions/teacherQuestionList";
import AssociateVideoQuestions from "./component/screen/video/associateVideoQuestions";
import ManageVideo from "./component/screen/video/manageVideo";
import Video from "./component/screen/video/video";
import ManageWorksheet from "./component/screen/worksheets/manageWorksheet";
import Worksheets from "./component/screen/worksheets/worksheets";
import Slides from "./component/screen/slides/slides";
import ManageSlide from "./component/screen/slides/manageSlide";
import SlideStandards from "./component/screen/slides/slideStandards";
import BadRequest from "./component/shared/common/badRequest";
import ErrorBoundaryComponent from "./component/shared/common/errorBoundary";
import IdleTimeoutPopup from "./component/shared/common/IdleTimeoutPopup";
import { ConfirmationServiceProvider } from "./component/shared/confirmation/confirmationService";
import Header from "./component/shared/layout/header";
import Sidebar from "./component/shared/layout/sidebar";
import { fetchProfile } from "./redux/thunks/userProfileThunk";
import Auth from "./utils/auth";
import constant from "./utils/constant";

interface ILayoutProps {
  checkIsDarkTheme: (flag: boolean) => void;
}

const Layout: FC<ILayoutProps> = ({ checkIsDarkTheme }) => (
  <>
    <Header checkIsDarkTheme={checkIsDarkTheme} />
    <div className="flex overflow-x-hidden">
      <Sidebar />
      <Outlet />
    </div>
  </>
);

// Save and Copy navigates to /questions/new from /questions/new; keying on the
// location forces a remount so the screen does not keep the question id it just
// saved and re-save it as a new question.
const AuthorNewQuestion: FC = () => {
  const routeLocation = useLocation();
  return <AuthorQuestion key={routeLocation.key} />;
};

const App: React.FC = (props: any) => {
  const navigate = useNavigate();
  const [errorFlag, setErrorFlag] = useState<boolean>(false);
  const [isDark, setIsDark] = useState<boolean>(false); /// unused variable, It is used to update this component virtually

  const isQaSite = location.href.includes("-qa.") ? true : false;
  const isDevSite = location.href.includes("-dev.") ? true : false;
  const publicPath = "/public/";

  useEffect(() => {
    const authToken = Auth.getAuthToken();
    if (authToken && authToken !== "") {
      props.setProfile(authToken);
    } else if (!location.pathname.startsWith(publicPath)) {
      const currentPath = window.location.pathname + window.location.search;
      // Don't set returnUrl if already going to login or root (which is also login)
      if (
        currentPath === "/" ||
        currentPath === "/login" ||
        currentPath.startsWith("/login?")
      ) {
        navigate("/login");
      } else {
        navigate(`/login?returnUrl=${encodeURIComponent(currentPath)}`);
      }
    }

    // Prevent scroll wheel from changing number input values
    const handleWheel = (e: WheelEvent) => {
      const target = e.target as HTMLElement;
      if (
        target &&
        target.tagName === "INPUT" &&
        (target as HTMLInputElement).type === "number"
      ) {
        if (document.activeElement === target) {
          e.preventDefault();
        }
      }
    };

    document.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      document.removeEventListener("wheel", handleWheel);
    };
  }, []);

  const checkIsDarkTheme = (flag) => {
    setIsDark(flag);
    localStorage.setItem("isDark", flag ? "1" : "0");

    const elem: any = document.getElementsByClassName("ant-select-selector");
    if (elem && elem.length && flag) {
      for (let i = 0; i < elem.length; i++) {
        elem[i].style.backgroundColor = "#1A1C23";
        elem[i].style.color = "#ffffff";
        elem[i].style.borderColor = "#6B7280";
      }
    } else if (elem.length && !flag) {
      for (let i = 0; i < elem.length; i++) {
        elem[i].style.backgroundColor = "unset";
        elem[i].style.color = "unset";
        elem[i].style.borderColor =
          "rgba(209, 213, 219, var(--tw-border-opacity))";
      }
    }
  };

  return (
    <>
      {(isQaSite || isDevSite) && (
        <div
          className={`w-full border-4 ${
            isDevSite ? "border-green-400" : isQaSite ? "border-blue-400" : ""
          } fixed pointer-events-none min-h-full z-50 flex justify-center`}
        ></div>
      )}

      <div className="flex overflow-hidden bg-transparent print-hide font-Open-Sans">
        <div className="flex flex-col flex-1 w-0 overflow-hidden bg-white dark:bg-custom-black">
          {!errorFlag && (
            <>
              <ConfirmationServiceProvider>
                <IdleTimeoutPopup />
                <ErrorBoundary
                  fallbackRender={() => {
                    return (
                      <div>
                        <ErrorBoundaryComponent
                          toggleErrorBoundary={() => {
                            setErrorFlag(false);
                          }}
                        />
                      </div>
                    );
                  }}
                >
                  <Routes>
                    <Route
                      path={constant.PUBLIC_ITEM_PREVIEW}
                      element={<PublicItemPreview />}
                    />
                    <Route
                      path={constant.DEFAULT}
                      element={<LoginComponent />}
                    />
                    <Route path="/login" element={<LoginComponent />} />
                    <Route
                      path={constant.BAD_REQUEST}
                      element={<BadRequest />}
                    />

                    <Route
                      element={<Layout checkIsDarkTheme={checkIsDarkTheme} />}
                    >
                      <Route path={constant.HOME} element={<HomeComponent />} />
                      <Route
                        path={constant.ACTIVITIES}
                        element={<ActivityReport />}
                      />
                      <Route path={constant.CONTENTS} element={<Content />} />
                      <Route
                        path={constant.ADDCONTENT}
                        element={<ManageContent />}
                      />
                      <Route
                        path={constant.EDITCONTENT}
                        element={<ManageContent />}
                      />
                      <Route path={constant.SUBJECTS} element={<Subject />} />
                      <Route
                        path={constant.SUPERSHEETS}
                        element={<Supersheet />}
                      />
                      <Route
                        path={constant.EDITSUPERSHEET}
                        element={<ManageSupersheet />}
                      />
                      <Route
                        path={constant.ADDSUPERSHEET}
                        element={<ManageSupersheet />}
                      />
                      <Route
                        path={constant.SUBJECTDETAIL}
                        element={<SubjectDetails />}
                      />
                      <Route
                        path={constant.ADDSUBJECT}
                        element={<SubjectDetails />}
                      />
                      <Route path={constant.GRADES} element={<Grade />} />
                      <Route
                        path={constant.ADDGRADE}
                        element={<ManageGrade />}
                      />
                      <Route
                        path={constant.EDITGRADE}
                        element={<ManageGrade />}
                      />
                      <Route path={constant.DOMAINS} element={<Domain />} />
                      <Route
                        path={constant.ADDDOMAIN}
                        element={<ManageDomain />}
                      />
                      <Route
                        path={constant.EDITDOMAIN}
                        element={<ManageDomain />}
                      />
                      <Route path={constant.PASSAGE} element={<Passage />} />
                      <Route
                        path={constant.ADDPASSAGE}
                        element={<ManagePassage />}
                      />
                      <Route
                        path={constant.EDITPASSAGE}
                        element={<ManagePassage />}
                      />
                      <Route
                        path={constant.SKILLPRACTICESUBJECT}
                        element={<SkillsPracticeSubject />}
                      />
                      <Route
                        path={constant.MANAGESKILLSUBJECT}
                        element={<ManageSkillsPracticeSubject />}
                      />
                      <Route
                        path={constant.ADDSKILLSUBJECT}
                        element={<ManageSkillsPracticeSubject />}
                      />
                      <Route
                        path={constant.SKILLPRACTICESTANDARD}
                        element={<SkillsPracticeStandards />}
                      />
                      <Route
                        path={constant.MANAGESKILLSTANDARD}
                        element={<ManageSkillsPracticeStandards />}
                      />
                      <Route
                        path={constant.ADDSKILLSTANDARD}
                        element={<ManageSkillsPracticeStandards />}
                      />
                      <Route
                        path={constant.SKILLPRACTICEQUESTION}
                        element={<PracticeQuestions />}
                      />
                      <Route
                        path={constant.MANAGESKILLQUESTION}
                        element={<ManagePracticeQuestions />}
                      />
                      <Route
                        path={constant.ADDSKILLQUESTION}
                        element={<ManagePracticeQuestions />}
                      />
                      <Route
                        path={constant.QUESTION}
                        element={<QuestionList />}
                      />
                      <Route
                        path={constant.AUTHORQUESTION}
                        element={<AuthorNewQuestion />}
                      />
                      <Route
                        path={constant.MANAGEQUESTION}
                        element={<AuthorQuestion key="edit" />}
                      />
                      <Route
                        path={constant.PASSAGEQUESTIONS}
                        element={<PassageQuestions />}
                      />
                      <Route path={constant.STANDARDS} element={<Standard />} />
                      <Route
                        path={constant.ADDSTANDARD}
                        element={<ManageStandard />}
                      />
                      <Route
                        path={constant.EDITSTANDARD}
                        element={<ManageStandard />}
                      />
                      <Route
                        path={constant.DASHBOARD}
                        element={<Dashboard />}
                      />
                      <Route path={constant.TAGS} element={<Tag />} />
                      <Route path={constant.ADDTAG} element={<ManageTag />} />
                      <Route path={constant.EDITTAG} element={<ManageTag />} />
                      <Route
                        path={constant.CANNEDTESTALL}
                        element={<CannedTests />}
                      />
                      <Route
                        path={constant.CANNEDTESTDETAIL}
                        element={<CannedTestEdit key="edit" />}
                      />
                      <Route
                        path={constant.CANNEDTEST}
                        element={<CannedTestEdit key="new" />}
                      />
                      <Route
                        path={constant.CANNEDTESTIMPORT}
                        element={<CannedTestImport />}
                      />
                      <Route
                        path={constant.CANNEDTESTDETAILIMPORT}
                        element={<CannedTestImport />}
                      />
                      <Route
                        path={constant.IMAGEGALLERY}
                        element={<ImageGallery />}
                      />
                      <Route
                        path={constant.STANDARDLINK}
                        element={<StandardLink />}
                      />
                      <Route
                        path={constant.SELLABLEPRODUCT}
                        element={<Sellableproduct />}
                      />
                      <Route
                        path={constant.ADDSELLABLEPRODUCT}
                        element={<ManageSellableproduct />}
                      />
                      <Route
                        path={constant.MANAGESELLABLEPRODUCT}
                        element={<ManageSellableproduct />}
                      />
                      <Route path={constant.BUNDLES} element={<Bundle />} />
                      <Route
                        path={constant.ADDBUNDLES}
                        element={<ManageBundle />}
                      />
                      <Route
                        path={constant.EDITBUNDLES}
                        element={<ManageBundle />}
                      />
                      <Route
                        path={constant.LIFTOFFSUBJECTEDIT}
                        element={<LiftOffSubject />}
                      />
                      <Route
                        path={constant.LIFTOFFSUBJECT}
                        element={<LiftOffSubject />}
                      />
                      <Route
                        path={constant.WORKSHEETS}
                        element={<Worksheets />}
                      />
                      <Route
                        path={constant.WORKSHEETEDIT}
                        element={<ManageWorksheet />}
                      />
                      <Route
                        path={constant.ADDWORKSHEET}
                        element={<ManageWorksheet />}
                      />
                      <Route path={constant.SLIDES} element={<Slides />} />
                      <Route
                        path={constant.ADDSLIDE}
                        element={<ManageSlide />}
                      />
                      <Route
                        path={constant.SLIDEEDIT}
                        element={<ManageSlide />}
                      />
                      <Route
                        path={constant.SLIDE_STANDARDS}
                        element={<SlideStandards />}
                      />
                      <Route path={constant.VIDEOS} element={<Video />} />
                      <Route
                        path={constant.VIDEOEDIT}
                        element={<ManageVideo />}
                      />
                      <Route
                        path={constant.LESSONPLANS}
                        element={<LessonPlans />}
                      />
                      <Route
                        path={constant.LESSONPLANS_NEW}
                        element={<LessonPlanEdit />}
                      />
                      <Route
                        path={constant.LESSONPLANS_EDIT}
                        element={<LessonPlanEdit />}
                      />
                      <Route
                        path={constant.LESSONPLANS_ASSETS_NEW}
                        element={<LessonPlanAssetEdit />}
                      />
                      <Route
                        path={constant.LESSONPLANS_ASSETS_EDIT}
                        element={<LessonPlanAssetEdit />}
                      />

                      <Route
                        path={constant.LEARNOSITY_UTILS_RESUBMIT}
                        element={<LearnosityResubmit />}
                      />
                      <Route path={constant.VIDEO} element={<ManageVideo />} />
                      <Route
                        path={constant.TEACHER_QUESTION_LIST}
                        element={<TeacherQuestionList />}
                      />
                      <Route
                        path={constant.ASSOCIATED_QUESTIONS}
                        element={<AccociatedQuestions />}
                      />
                      <Route
                        path={constant.STANDARDGROUPS}
                        element={<StandardGroup />}
                      />
                      <Route
                        path={constant.STANDARDGROUPEDIT}
                        element={<StandardGroupEdit />}
                      />
                      <Route
                        path={constant.VIDEO_ASSOCIATED_QUESTIONS}
                        element={<AssociateVideoQuestions />}
                      />
                      <Route
                        path={constant.PERMISSIONS}
                        element={<Permissions />}
                      />
                      <Route
                        path={constant.PERMISSION_GROUPS}
                        element={<PermissionGroup />}
                      />
                      <Route
                        path={constant.ADD_PERMISSION_GROUP}
                        element={<ManagePermissionGroup />}
                      />
                      <Route
                        path={constant.EDIT_PERMISSION_GROUP}
                        element={<ManagePermissionGroup />}
                      />
                      <Route
                        path={constant.EDIT_PERMISSION}
                        element={<ManagePermission />}
                      />
                      <Route
                        path={constant.ADD_PERMISSION}
                        element={<ManagePermission />}
                      />
                      <Route
                        path={constant.ADD_USER_PERMISSION}
                        element={<ManageUserPermission />}
                      />
                      <Route
                        path={constant.EDIT_USER_PERMISSION}
                        element={<ManageUserPermission />}
                      />
                      <Route
                        path={constant.USER_PERMISSIONS}
                        element={<UserPermissions />}
                      />
                      <Route
                        path={constant.DEFAULT_SUBJECTS}
                        element={<DefaultSubjects />}
                      />
                      <Route path={constant.CRM} element={<CRM />} />
                      <Route
                        path={constant.crmAddUser}
                        element={<ManageCrm />}
                      />
                      <Route
                        path={constant.crmAddSchool}
                        element={<ManageCrm />}
                      />
                      <Route
                        path={constant.crmAddDistrict}
                        element={<ManageCrm />}
                      />
                      <Route
                        path={constant.SCHOOLDETAIL}
                        element={<SchoolDetail />}
                      />
                      <Route
                        path={constant.DISTRICTDETAIL}
                        element={<DistrictDetail />}
                      />
                      <Route
                        path={constant.crmUserContent}
                        element={<UserContentLayout />}
                      >
                        <Route index element={<UserContent />} />
                        <Route path="passages" element={<Passages />} />
                        <Route
                          path="passages/:teacherPassageId"
                          element={<TeacherPassageEditor />}
                        />
                        <Route path="questions" element={<Questions />} />
                        <Route path="questions/new" element={<QuestionNew />} />
                        <Route
                          path="questions/:teacherQuestionId"
                          element={<QuestionEditorRoute />}
                        />
                        <Route path="assessments" element={<Assessments />} />
                        <Route
                          path="assessments/new"
                          element={<AssessmentNew />}
                        />
                        <Route
                          path="assessments/:assessmentId"
                          element={<AssessmentEdit />}
                        />
                      </Route>
                      <Route
                        path={constant.ClassifyCleanup}
                        element={<ClassifyCleanup />}
                      />
                      <Route
                        path={constant.STANDARDS_MAGIC_MATCH}
                        element={<MagicMatch />}
                      />
                    </Route>
                  </Routes>
                </ErrorBoundary>
              </ConfirmationServiceProvider>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    setProfile: (token: string) => {
      dispatch(fetchProfile(token));
    },
  };
};

const mapStateToProps = (state: any) => {
  return {
    profile: state.profile,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
